import React, { useCallback, useContext, useMemo } from 'react';
import { QueryClient, UseInfiniteQueryResult, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import { AppContext } from 'views/App/context';
import useUrlParams from 'hooks/useUrlParams';
import useIssuedManuals from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManuals';
import {
  ManualItem,
  ManualPermissions,
  IssuedManualsUrlParams,
  IssuedManualDownloadType,
} from '../_types/manuals/issued';
import { ApiCollectionResponse } from 'types/api_response';
import { ServerErrorResponse } from 'data_sources/constants';
import useStickyState from 'hooks/useStickyState';
import contentHelper from 'helpers/contentHelper';
import { getIssuedManualContentPdf, ExportContentParams } from './_data_sources/useIssuedManualContentPdf';

type IssuedManualsProviderProps = {
  children: React.ReactNode;
};

export type IssuedManualsProviderResponse = {
  clearParams: () => void;
  params: IssuedManualsUrlParams;
  queryClient: QueryClient | null;
  updateParams: (updatedParams: IssuedManualsUrlParams) => void;
  manuals: ManualItem[];
  manualsQuery: UseInfiniteQueryResult<ApiCollectionResponse<ManualItem>, ServerErrorResponse>;
  manualSchema: any;
  manualPermissions: ManualPermissions | null;
  orgId: string;
  manualHasPermissions: (manualId: string, keys: string[]) => boolean;
  handleDownload: (
    manualId: string,
    itemId: string,
    type: IssuedManualDownloadType,
    title?: string,
    exportParams?: ExportContentParams
  ) => Promise<void>;
  showAccreditationTags: boolean;
  setShowAccreditationTags: (show: boolean) => void;
};

const defaultParams: IssuedManualsUrlParams = {
  search: null,
  orgId: null,
  jurisdictionId: null,
};

export const IssuedManualsContext = React.createContext<IssuedManualsProviderResponse>(
  {} as IssuedManualsProviderResponse
);

const IssuedManualsProvider: ({ children }: IssuedManualsProviderProps) => any = ({ children }) => {
  const queryClient = useQueryClient();
  const { currentOrganizationId, currentProfile } = useContext(AppContext);
  const orgId = currentOrganizationId;
  const [params, setParams] = useUrlParams(defaultParams);
  const [manualPermissions, setManualPermissions] = useStickyState<ManualPermissions | null>(
    'phx-manualPermissions',
    null
  );
  const [showAccreditationTags, setShowAccreditationTags] = useStickyState('phx-showAccreditationTags-issued', true);
  const updateParams = useCallback(
    (updatedParams: IssuedManualsUrlParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const newParams: any = {
      ...params,
    };
    Object.keys(newParams).forEach((key) => {
      if (newParams[key] === null || newParams[key] === '') {
        delete newParams[key];
      }
    });

    if (currentProfile?.user?.orgId && currentOrganizationId !== currentProfile?.user?.orgId) {
      newParams.orgId = currentOrganizationId;
    }

    return newParams;
  }, [params, currentProfile?.user?.orgId, currentOrganizationId]);

  const manualData = useIssuedManuals(orgId, queryParams, {
    onSuccess: (data: any) => {
      const mPerms: ManualPermissions = {};
      (data.pages ? data.pages.flatMap((x: any) => x.content) : ([] as ManualItem[])).forEach((m: ManualItem) => {
        mPerms[m.id] = (m.authorizations ?? []).map((x) => x.action).sort();
      });
      setManualPermissions(mPerms);
    },
  });

  const manualHasPermissions = useCallback(
    (manualId: string, keys: string[]) => {
      let permissionKeys: string[] = [];
      if (manualPermissions && manualPermissions[manualId]) {
        permissionKeys = manualPermissions[manualId];
      }
      return keys.every((x) => permissionKeys.map((x) => x.toLowerCase()).includes(x.toLowerCase()));
    },
    [manualPermissions]
  );

  const manualSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required('Name is required'),
  });

  const manuals = useMemo(
    () => (manualData.data && manualData.data.pages ? manualData.data.pages.flatMap((x) => x.content) : []),
    [manualData.data]
  );
  const handleDownload = useCallback(
    async (
      manualId: string,
      itemId: string,
      type: IssuedManualDownloadType,
      title?: string,
      exportParams?: ExportContentParams
    ) => {
      const fileContent = await getIssuedManualContentPdf(manualId, itemId, type, exportParams);
      if (fileContent) {
        const bytes = new Uint8Array(fileContent.length);
        for (let i = 0; i < fileContent.length; i++) {
          bytes[i] = fileContent.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const href = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', contentHelper.parameterize(title || 'download'));
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }
    },
    []
  );

  const contextValue: IssuedManualsProviderResponse = useMemo(
    () => ({
      clearParams,
      params,
      queryClient,
      updateParams,
      manualsQuery: manualData,
      manuals: manuals,
      manualPermissions,
      manualHasPermissions,
      manualSchema,
      orgId,
      handleDownload,
      showAccreditationTags,
      setShowAccreditationTags,
    }),
    [
      clearParams,
      params,
      queryClient,
      updateParams,
      manualData,
      manuals,
      manualPermissions,
      manualHasPermissions,
      manualSchema,
      orgId,
      handleDownload,
      showAccreditationTags,
      setShowAccreditationTags,
    ]
  );

  return <IssuedManualsContext.Provider value={contextValue}>{children}</IssuedManualsContext.Provider>;
};

export default IssuedManualsProvider;
